import { MD5 } from 'crypto-js'
import { getFingerprint } from '@/utils/fingerprint'
import { PIXEL_EVENTS, PIXEL_STORAGE_KEYS } from '~/utils/constants'

function parseUserAgent(uaString) {
  let browserName = 'Unknown'
  let browserVersion = 'Unknown'
  let deviceType = 'desktop'
  let osName = 'Unknown'
  let osVersion = 'Unknown'

  // Detect device type
  if (/Mobi|Android|iPhone|iPad|iPod/i.test(uaString)) {
    deviceType = 'mobile'
  }

  // Detect OS
  if (/Windows NT ([\d.]+)/i.test(uaString)) {
    osName = 'Windows'
    const version = uaString.match(/Windows NT ([\d.]+)/i)[1]
    const versionMap = {
      '10.0': '10',
      6.3: '8.1',
      6.2: '8',
      6.1: '7',
      '6.0': 'Vista',
      5.1: 'XP'
    }
    osVersion = versionMap[version] || version
  } else if (/Mac OS X ([\d_]+)/i.test(uaString)) {
    osName = 'Mac OS X'
    osVersion = uaString.match(/Mac OS X ([\d_]+)/i)[1].replace(/_/g, '.')
  } else if (/Android ([\d.]+)/i.test(uaString)) {
    osName = 'Android'
    osVersion = uaString.match(/Android ([\d.]+)/i)[1]
  } else if (/iPhone OS ([\d_]+)/i.test(uaString)) {
    osName = 'iOS'
    osVersion = uaString.match(/iPhone OS ([\d_]+)/i)[1].replace(/_/g, '.')
  } else if (/iPad; CPU OS ([\d_]+)/i.test(uaString)) {
    osName = 'iOS'
    osVersion = uaString.match(/iPad; CPU OS ([\d_]+)/i)[1].replace(/_/g, '.')
  } else if (/Linux/i.test(uaString)) {
    osName = 'Linux'
  }

  // Detect browsers
  if (/Chrome\/([\d.]+)/i.test(uaString) && !/Edge\/|Edg\/|OPR\//.test(uaString)) {
    browserName = 'Chrome'
    browserVersion = uaString.match(/Chrome\/([\d.]+)/i)[1]
  } else if (/Safari\/([\d.]+)/i.test(uaString) && /Version\/([\d.]+)/i.test(uaString)) {
    browserName = 'Safari'
    browserVersion = uaString.match(/Version\/([\d.]+)/i)[1]
  } else if (/Firefox\/([\d.]+)/i.test(uaString)) {
    browserName = 'Firefox'
    browserVersion = uaString.match(/Firefox\/([\d.]+)/i)[1]
  } else if (/Edg\/([\d.]+)/i.test(uaString)) {
    browserName = 'Edge'
    browserVersion = uaString.match(/Edg\/([\d.]+)/i)[1]
  } else if (/OPR\/([\d.]+)/i.test(uaString)) {
    browserName = 'Opera'
    browserVersion = uaString.match(/OPR\/([\d.]+)/i)[1]
  }

  return {
    browser_name: browserName,
    browser_version: browserVersion,
    device_type: deviceType,
    os_name: osName,
    os_version: osVersion
  }
}

function getUTCOffset() {
  const offsetMinutes = new Date().getTimezoneOffset() // in minutes, opposite sign
  const sign = offsetMinutes <= 0 ? '+' : '-'
  const hours = String(Math.floor(Math.abs(offsetMinutes) / 60))
  const minutes = String(Math.abs(offsetMinutes) % 60).padStart(2, '0')

  // If minutes is "00", omit it for cleaner output
  return minutes === '00' ? `UTC${sign}${hours}` : `UTC${sign}${hours}:${minutes}`
}

const pixelId = process.client ? localStorage.getItem('pixel_id') : ''
const bannerId = process.client ? localStorage.getItem('banner_id') : ''

export const state = () => ({
  pixelId,
  bannerId,
  isForPwaOnly: false,
  isForH5Only: false
})

export const actions = {
  generateEventBody(
    { state, rootState },
    { event_name: eventName, player_id: playerIdParam, value, bank_account_id: bankAccountId }
  ) {
    const pixelId = state.pixelId
    const bannerId = state.bannerId
    const playerId = playerIdParam || rootState.player.player.phone
    const {
      browser_name: browserName,
      browser_version: browserVersion,
      device_type: deviceType,
      os_name: osName
    } = parseUserAgent(navigator.userAgent)

    // Get fbc and fbp from cookies
    const fbc = this.$cookie.get('_fbc') || ''
    const fbp = this.$cookie.get('_fbp') || ''

    const fingerprint = getFingerprint()?.toString()
    const timestamp = new Date().getTime()
    const hashedPlayerId = MD5(playerId ?? '').toString()
    const hashedPlayerBankAccountId = bankAccountId ? MD5(bankAccountId)?.toString() : null

    const eventId = `${eventName}_${playerId ? hashedPlayerId : fingerprint}_${timestamp}`

    // By default event status will always be 1
    let eventStatus = 1
    if (
      [
        PIXEL_EVENTS.downloadAction,
        PIXEL_EVENTS.firstView,
        PIXEL_EVENTS.contact,
        PIXEL_EVENTS.registration,
        PIXEL_EVENTS.promoClaim
      ].includes(eventName)
    ) {
      eventStatus = 2
    }

    const eventData = {
      event_id: eventId,
      event_name: eventName,
      event_source_url: window.location.href,
      user_agent: navigator.userAgent,
      fbp,
      fbc,
      page_title: document.title,
      fingerprint,
      browser_name: browserName,
      browser_version: browserVersion,
      os: osName,
      device_type: deviceType,
      browser_language: navigator.language,
      browser_viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      timezone: getUTCOffset(),
      event_action_source: 'website',
      value: value ?? 0, // default value for backend purpose
      custom_event_data: {
        event_id: eventId,
        hashed_user_id: hashedPlayerId,
        bank_account_id: hashedPlayerBankAccountId,
        registration_time: new Date().toISOString(),
        value: value ?? 0,
        os: osName,
        browser_language: navigator.language,
        screen_resolution: `${window.innerWidth}x${window.innerHeight}`,
        device_type: deviceType,
        page_title: document.title
        // currency: this.$cookie.get('currency')
      }, // default value for backend purpose
      event_status: eventStatus, // default value for backend purpose
      transaction_id: null, // default value for backend purpose
      // New requirement from backend team
      bank_account_id: hashedPlayerBankAccountId
    }

    // Change source_url to be referrer url
    if (this.$cookie.get('x-banner-url')) {
      eventData.event_source_url = this.$cookie.get('x-banner-url')
    }

    const body = {
      px_id: pixelId,
      banner_id: bannerId,
      event_data: [eventData],
      player_id: hashedPlayerId,
      bank_account_id: hashedPlayerBankAccountId
    }
    return body
  },
  // Add Conversion API Event
  async addPixelEvent({ dispatch }, body) {
    try {
      const response = await this.$axios.post(
        `${this.$config.BANNER_REDIRECTION_URL}/px/banners/events/conv`,
        body
      )
      return response
    } catch (error) {
      return false
    }
  },
  async getPixelBannerStatus({ commit, state }, { xDirectBannerId, xBannerPixelId }) {
    try {
      const headers = {}
      if (xDirectBannerId) headers['x-direct-banner-id'] = xDirectBannerId
      if (xBannerPixelId) headers['x-banner-pixel-id'] = xBannerPixelId

      const response = await this.$axios.get('/banners/pixel', {
        headers
      })
      if (response.data) {
        // Handle response headers
        if (response.data.status) {
          const pixelExpirationDate = new Date()
          pixelExpirationDate.setDate(new Date().getDate() + 60)
          const xBannerPixelId = response.headers['x-banner-pixel-id']
          const xBannerAdType = response.headers['x-banner-ad-type']
          const xBannerId = response.headers['x-banner-id']

          if (xBannerPixelId) localStorage.setItem(PIXEL_STORAGE_KEYS.pixelId, xBannerPixelId)
          if (xBannerAdType) localStorage.setItem(PIXEL_STORAGE_KEYS.bannerAdType, xBannerAdType)
          if (xBannerId) localStorage.setItem(PIXEL_STORAGE_KEYS.bannerId, xBannerId)
          if (xBannerPixelId)
            localStorage.setItem(
              PIXEL_STORAGE_KEYS.pixelExpiredStorage,
              pixelExpirationDate.toISOString()
            )

          if (xBannerPixelId) commit('setPixelId', xBannerPixelId)
          if (xBannerId) commit('setBannerId', xBannerId)

          return {
            status: response.data.status,
            pixel_id: xBannerPixelId,
            banner_id: xBannerId,
            ad_type: xBannerAdType
          }
        }
      }
      return false
    } catch (error) {
      return false
    }
  }
}

export const mutations = {
  setPixelId(state, pixelId) {
    state.pixelId = pixelId
  },
  setBannerId(state, bannerId) {
    state.bannerId = bannerId
  },
  setIsPwa(state, isPwa) {
    state.isForPwaOnly = isPwa
  },
  setIsH5(state, isH5) {
    state.isForH5Only = isH5
  }
}
